import { authenticationStore } from "@/state/auth/AuthStore";
import { ActiveUser, AuthenticationContext } from "@/type/core/auth/auth.types";
import { ExtraRecipient } from "@/type/core/transaction/transaction.types";
import BasicButton from "@components/core/buttons/BasicButton";
import AddSignatoryForm from "@components/core/forms/AddSignatoryForm";
import ContactForm from "@components/core/forms/ContactForm";
import BasicModal from "@components/core/modals/BasicModal";
import ContactComparisonModal from "@components/core/modals/ContactComparisonModal";
import InfoModal from "@components/core/modals/InfoModal";
import ContactsIcon from "@static/core/icons/icon-contacts.svg?react";
import DeleteIcon from "@static/core/icons/icon-delete.svg?react";
import UserIcon from "@static/core/icons/icon-user.svg?react";
import { Contact } from "@type/core/dashboard/dashboard.types";
import { ContactSort, Office } from "@type/core/offices/offices.types";
import { Col, Row } from "antd";
import { useForm } from "antd/es/form/Form";
import { useUnit } from "effector-react";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

interface RecipientProps {
  office: Office;
  isAddBillingEmail?: boolean;
  isFromScellement?: boolean;
  className?: string;
  contacts?: Contact[];
  members?: Contact[];
  emailFacturationAddress?: ContactSort[];
  onChangeEmailFacturationAddress?: (
    emailFacturationAddress: ContactSort[],
  ) => void;
  onAddMe?: (me?: ActiveUser) => void;
  recipients?: ExtraRecipient[];
  setRecipients?: (recipients: ExtraRecipient[]) => void;
}

const Recipient: FunctionComponent<RecipientProps> = ({
  office,
  isAddBillingEmail = false,
  isFromScellement = false,
  className = "",
  contacts = [],
  members = [],
  emailFacturationAddress,
  onChangeEmailFacturationAddress,
  onAddMe,
  recipients,
  setRecipients,
}) => {
  const signatureRight = office.rights?.includes("SIGNATURE_COLLECTE_SIGNER");
  const isExpertComptable = office.expertComptable;
  const hasOfficeContactsDisabled = office.isContactDisabled;

  const { t } = useTranslation();

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);

  /* States */
  const [isAddExpertModalVisible, setIsAddExpertModalVisible] =
    useState<boolean>(false);
  const [isAddContactModalVisible, setIsAddContactModalVisible] =
    useState<boolean>(false);
  const [
    isEmailAlreadyExistsModalVisible,
    setIsEmailAlreadyExistsModalVisible,
  ] = useState<boolean>(false);
  const [
    isContactAlreadyExistsModalVisible,
    setIsContactAlreadyExistsModalVisible,
  ] = useState<boolean>(false);
  const [fieldsSearch, setFieldsSearch] = useState<string>("");
  const [categoriesSearch, setCategoriesSearch] = useState<string[]>([]);
  const [contactToAdd, setContactToAdd] = useState<Contact>();
  const [existingContact, setExistingContact] = useState<Contact>();
  /* States */

  const [signatoryForm] = useForm();

  const buttons = [
    {
      id: "me",
      label: t("signature.accordion.signatoryForm.addMe"),
      icon: <></>,
      disabled:
        !isAddBillingEmail ||
        (emailFacturationAddress &&
          emailFacturationAddress &&
          emailFacturationAddress.find(
            (contact) => contact.email === authStore?.user?.email,
          )),
      onClick: () => onAddMe?.(authStore?.user),
    },
    {
      label: t("signature.accordion.signatoryForm.addExpert"),
      icon: <UserIcon />,
      onClick: () => {
        setIsAddExpertModalVisible(true);
      },
      disabled: contacts.length < 1,
    },
    {
      label: t("signature.accordion.signatoryForm.addContact"),
      icon: <ContactsIcon />,
      onClick: () => {
        setIsAddContactModalVisible(true);
      },
      disabled: false,
    },
  ];

  const handleDeleteEmailFacturationAddress = (email: string) => {
    if (emailFacturationAddress) {
      const newEmailFacturationAdresses = emailFacturationAddress?.filter(
        (contactToDelete) => contactToDelete.email !== email,
      );
      onChangeEmailFacturationAddress?.(newEmailFacturationAdresses);
    }
  };

  const onModalCancel = () => {
    setFieldsSearch("");
    signatoryForm.resetFields();
  };

  return (
    <div className={`app-recipient mb-16 ${className}`}>
      <div className="mt-16 mb-8 text-dark">
        {isAddBillingEmail
          ? t("signature.accordion.parametersForm.billingEmail.label")
          : t("signature.accordion.parametersForm.recipient.label")}
        {isFromScellement && <InfoModal value="sealRecipient" info />}
        {!isFromScellement && !isAddBillingEmail && (
          <InfoModal value="recipient" info />
        )}
      </div>
      <div className="d-flex align-items-center">
        <ul className="list-inline">
          {buttons.map((button, index) => {
            if (button.id !== "me" || isExpertComptable || signatureRight) {
              return (
                <li key={"button" + index} className="list-inline-item">
                  <BasicButton
                    variant="transparent-tertiary"
                    icon={button.icon}
                    text={button.label}
                    onClick={button.onClick}
                    disabled={!!button.disabled}
                  />
                </li>
              );
            } else {
              return null;
            }
          })}
        </ul>
      </div>
      <div className="d-flex justify-content-center">
        <ul className="mt-16">
          {!emailFacturationAddress &&
            recipients &&
            setRecipients &&
            recipients.map((recipient) => {
              if (
                !!recipient.firstname &&
                !!recipient.lastname &&
                !!recipient.email
              ) {
                return (
                  <li key={recipient.email} className="w-100">
                    <div className="d-flex align-items-center justify-content-between">
                      {`${recipient.firstname} ${recipient.lastname} (${recipient.email})`}
                      <div
                        className="d-flex ml-48"
                        onClick={() =>
                          setRecipients(
                            recipients.filter(
                              (r) => r.email !== recipient.email,
                            ),
                          )
                        }
                      >
                        <DeleteIcon className="picto icon-30" />
                      </div>
                    </div>
                  </li>
                );
              } else {
                return (
                  <li key={recipient.email} className="w-100">
                    <div className="d-flex align-items-center justify-content-between">
                      {recipient.email}
                      <div
                        className="d-flex ml-48"
                        onClick={() =>
                          setRecipients(
                            recipients.filter(
                              (r) => r.email !== recipient.email,
                            ),
                          )
                        }
                      >
                        <DeleteIcon className="picto icon-30" />
                      </div>
                    </div>
                  </li>
                );
              }
            })}
          {emailFacturationAddress?.map((contact) => {
            return (
              <li key={contact.email} className="w-100">
                <div className="d-flex align-items-center justify-content-between">
                  {contact.firstname && contact.lastname
                    ? `${contact.firstname} ${contact.lastname} (${contact.email})`
                    : contact.email}
                  <div
                    className="d-flex ml-48"
                    onClick={() =>
                      handleDeleteEmailFacturationAddress(contact.email)
                    }
                  >
                    <DeleteIcon className="picto icon-30" />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <BasicModal
        visible={isAddExpertModalVisible}
        onCancel={() => {
          setIsAddExpertModalVisible(false);
          onModalCancel();
        }}
        footer={null}
        title={t("signature.accordion.parametersForm.recipient.addRecipient")}
      >
        <AddSignatoryForm
          baseContacts={members.filter(
            (member) => member.email !== authStore?.user?.email,
          )}
          searchDisabled={!!hasOfficeContactsDisabled}
          signatories={emailFacturationAddress ?? recipients ?? []}
          setSignatories={(signatories) => {
            onChangeEmailFacturationAddress?.(signatories);
            setRecipients?.(signatories);
          }}
          setIsModalVisible={setIsAddExpertModalVisible}
          fromMembers
          form={signatoryForm}
          fieldsSearch={fieldsSearch}
          setFieldsSearch={setFieldsSearch}
          categoriesSearch={categoriesSearch}
          setCategoriesSearch={setCategoriesSearch}
        />
      </BasicModal>
      <BasicModal
        visible={isAddContactModalVisible}
        onCancel={() => {
          setIsAddContactModalVisible(false);
          onModalCancel();
          setCategoriesSearch([]);
        }}
        footer={null}
        title={t("signature.accordion.parametersForm.recipient.addRecipient")}
        className="modal-lg"
        destroyOnClose
      >
        <Row gutter={32}>
          <Col span={24} md={14} className="d-flex flex-column">
            <h2 className="text-primary py-24">
              {t("signature.accordion.signatoryForm.addFromContacts")}
              <InfoModal value="pickFromContacts" info />
              {!!hasOfficeContactsDisabled && (
                <h4 className="text-orange">
                  {t("signature.addContactModal.contactDisabled")}
                </h4>
              )}
            </h2>
            <AddSignatoryForm
              baseContacts={contacts}
              searchDisabled={!!hasOfficeContactsDisabled}
              signatories={emailFacturationAddress ?? recipients ?? []}
              setSignatories={(signatories) => {
                onChangeEmailFacturationAddress?.(signatories);
                setRecipients?.(signatories);
              }}
              setIsModalVisible={setIsAddContactModalVisible}
              form={signatoryForm}
              fieldsSearch={fieldsSearch}
              setFieldsSearch={setFieldsSearch}
              categoriesSearch={categoriesSearch}
              setCategoriesSearch={setCategoriesSearch}
            />
          </Col>
          <Col
            span={24}
            md={10}
            className="d-flex flex-column border-left-secondary"
          >
            <h2 className="text-primary py-24">
              {t(
                "signature.accordion.parametersForm.recipient.createRecipient",
              )}
            </h2>
            <ContactForm
              isAddRecipient
              signatories={emailFacturationAddress ?? recipients ?? []}
              setSignatories={(signatories) => {
                onChangeEmailFacturationAddress?.(signatories);
                setRecipients?.(signatories);
              }}
              officeId={office.id}
              baseContacts={contacts}
              setIsContactAlreadyExistsModalVisible={
                setIsContactAlreadyExistsModalVisible
              }
              setContactToAdd={setContactToAdd}
              setExistingContact={setExistingContact}
              onSubmit={() => setIsAddContactModalVisible(false)}
            />
          </Col>
        </Row>
      </BasicModal>
      <BasicModal
        visible={isEmailAlreadyExistsModalVisible}
        onCancel={() => setIsEmailAlreadyExistsModalVisible(false)}
        footer={
          <BasicButton
            variant="primary"
            text={t("modals.info.dismiss")}
            onClick={() => setIsEmailAlreadyExistsModalVisible(false)}
            className="w-100"
          />
        }
        title={t("modal.warning")}
      >
        {t("signature.accordion.parametersForm.recipient.emailAlreadyExists")}
      </BasicModal>
      {existingContact && contactToAdd && (
        <ContactComparisonModal
          visible={isContactAlreadyExistsModalVisible}
          onCancel={() => setIsContactAlreadyExistsModalVisible(false)}
          existingContact={existingContact}
          contactToAdd={contactToAdd}
          onContactChoose={(selectedContact) => {
            setRecipients?.([
              ...(recipients ?? []),
              {
                ...selectedContact,
                key: selectedContact.email + Date.now().toString(),
              },
            ]);
            setIsContactAlreadyExistsModalVisible(false);
          }}
        />
      )}
    </div>
  );
};

export default Recipient;
